import GrowthPanel, { trayCount } from '../../culture/GrowthPanel';
import TrendLine from '../../charts/TrendLine';
import RoiHistogram from '../../thermal/RoiHistogram';
import { can } from '../../../utils/permissions';
import { resolvePhotoUrl } from '../../../utils/photoUrl';
import { formatCelsius, formatDelta } from '../../../utils/thermal/legend';
import { targetSeries } from '../../../utils/thermal/series';
import { startIsolateBatch } from '../../../utils/spores/sporeBatch';
import { culturePath, sporePath, trayTools } from '../LabAnalyticsTray';
import { analysisLine } from './MicroscopyTab';
import * as S from '../../specimens/styles';

/*
 * Analytics (plan phase 9, docs/UI_HUBS_AND_GESTURES_PLAN.md 4.3): what the
 * analysis tools measured on this isolate, from its dossier only -- so a
 * redacted (scope-only) isolate shows what its dossier allows and nothing
 * more. Three sections, each a labelled region:
 *
 *   Growth     each plate's petri dish readings as growth curves
 *              (`culture_growth`, components/culture/GrowthPanel.js); a
 *              reading read off a tray photograph (several dishes on one,
 *              migration 0101) is marked "measured on a tray". Its way into
 *              the dish scan station on this isolate, for whoever the
 *              station lets in (LabAnalyticsTray's trayTools / culturePath).
 *   Spores     how many micrographs, how many spore analyses, the newest
 *              few (`microscopy`, `spore_analyses`); the analysis
 *              workbench's microscopy mode on this isolate
 *              (/tools/analysis?mode=microscopy&isolate=, with its
 *              micrographs as the batch) for game:play, and the Microscopy
 *              tab for everyone.
 *   Thermal    ThermalRoiSection (plan phase 10), when the dossier carries
 *              `thermal_rois`: each record's heat profile and a card per
 *              region (values, deltas, histogram, treatment, protocol).
 */
export const LATEST_SPORES = 3;

export default function AnalyticsTab({ item, dossier, user, t, onNavigate, onTab = null }) {
  const curves = (dossier.culture_growth || []).filter(c => c?.points?.length);
  const trayed = curves.reduce((n, c) => n + trayCount(c), 0);
  const micrographs = dossier.microscopy || [];
  const analyses = dossier.spore_analyses || [];
  const tools = trayTools(item, user);
  const base = `hub-analytics-${item.specimen_id}`;
  const openSpores = () => {
    startIsolateBatch(item, dossier);
    onNavigate(sporePath(item));
  };
  return (
    <div className="hub-analytics" data-testid={base} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section aria-labelledby={`${base}-growth`} data-testid={`${base}-growth`} style={sectionStyle}>
        <h4 id={`${base}-growth`} style={headingStyle}>{t('analytics.growth.title')}</h4>
        <GrowthPanel curves={curves} sets testId={`${base}-growth-panel`} />
        {trayed > 0 && (
          <p style={S.hint} data-testid={`${base}-tray-note`}>{t('analytics.growth.trayNote', { count: trayed })}</p>
        )}
        {tools.includes('culture') && (
          <div className="hub-tab-actions">
            <button type="button" style={S.ghostBtn} data-testid={`${base}-culture`} title={t('analytics.growth.openTip')}
                    onClick={() => onNavigate(culturePath(item))}>
              {t('analytics.growth.open')}
            </button>
          </div>
        )}
      </section>

      <section aria-labelledby={`${base}-spores`} data-testid={`${base}-spores`} style={sectionStyle}>
        <h4 id={`${base}-spores`} style={headingStyle}>{t('analytics.spores.title')}</h4>
        <p style={S.hint} data-testid={`${base}-spore-counts`}>
          {t('analytics.spores.counts', { micrographs: micrographs.length, analyses: analyses.length })}
        </p>
        {analyses.length > 0 && (
          <ul style={listStyle}>
            {analyses.slice(0, LATEST_SPORES).map(a => (
              <li key={a.analysis_id || a.image_id} data-testid={`${base}-spore-${a.image_id}`}>
                <bdi dir="ltr">{analysisLine(a, t)}</bdi>
              </li>
            ))}
          </ul>
        )}
        <div className="hub-tab-actions">
          {can(user, 'game:play') && micrographs.length > 0 && (
            <button type="button" style={S.ghostBtn} data-testid={`${base}-spore-workbench`}
                    title={t('analytics.spores.workbenchTip')} onClick={openSpores}>
              {t('analytics.spores.workbench')}
            </button>
          )}
          {onTab && (
            <button type="button" style={S.ghostBtn} data-testid={`${base}-microscopy`}
                    title={t('analytics.spores.microscopyTip')} onClick={() => onTab('microscopy')}>
              {t('analytics.spores.microscopy')}
            </button>
          )}
        </div>
      </section>

      {Array.isArray(dossier.thermal_rois) && (
        <ThermalRoiSection rois={dossier.thermal_rois} base={base} t={t} item={item} dossier={dossier} user={user}
                           onNavigate={onNavigate} />
      )}
    </div>
  );
}

/**
 * Thermal ROI cards (plan phase 10). The dossier's `thermal_rois` -- one
 * statement, filtered by THERMAL_VISIBLE_SQL (routes/specimen_overview.py
 * _thermal_rois) -- are the regions of each capture's current analysis tied to
 * this isolate, its origin, its plates or its slants, newest capture first.
 *
 *   Heat profile  per record with two or more captures, its regions' mean over
 *                 time (utils/thermal/series.js targetSeries; several regions of
 *                 one capture on one record are one point, their mean and range)
 *   Cards         one per region: what it is of, when the capture was taken
 *                 (its thumbnail), T min / max / mean, the differences from the
 *                 analysis's reference region, its histogram over the
 *                 analysis's range, the treatment, the unit and the protocol
 *
 * Only what the dossier gives: a redacted reader's has no origin's regions and
 * no who-measured or note, and nothing here asks for more. When the dossier
 * was cut short (`truncated`), the section says so.
 */
export function ThermalRoiSection({ rois, base, t, dossier }) {
  const profiles = targetSeries(rois).filter(s => s.points.length >= 2);
  return (
    <section aria-labelledby={`${base}-thermal`} data-testid={`${base}-thermal`} style={sectionStyle}>
      <h4 id={`${base}-thermal`} style={headingStyle}>{t('analytics.thermal.title')}</h4>
      {!rois.length && <p style={S.hint}>{t('analytics.thermal.none')}</p>}
      {profiles.map(s => (
        <figure key={s.key} style={{ margin: 0 }} data-testid={`${base}-thermal-profile-${s.kind}-${s.id}`}>
          <figcaption style={{ fontSize: 13, fontWeight: 600 }}>
            {t('analytics.thermal.profile', { record: recordName(t, s) })}
          </figcaption>
          <TrendLine series={[{ ...s, name: recordName(t, s) }]} yLabel={t('analytics.thermal.axis')}
                     formatValue={v => formatCelsius(v, 1)} testId={`${base}-thermal-trend-${s.id}`}
                     pointLabel={(p) => t('analytics.thermal.point', { when: new Date(p.t).toISOString().slice(0, 10),
                       value: formatCelsius(p.value, 1), n: p.n })} />
        </figure>
      ))}
      {rois.length > 0 && (
        <ul style={cardsStyle} data-testid={`${base}-thermal-cards`}>
          {rois.map(r => <ThermalRoiCard key={r.roi_id} roi={r} base={base} t={t} />)}
        </ul>
      )}
      {dossier?.truncated && rois.length > 0 && (
        <p style={S.hint} data-testid={`${base}-thermal-truncated`}>{t('analytics.thermal.truncated')}</p>
      )}
    </section>
  );
}

const recordName = (t, target) => t(`analytics.thermal.kind.${target.kind}`, { code: target.code || '' });

function ThermalRoiCard({ roi, base, t }) {
  const id = `${base}-thermal-${roi.roi_id}`;
  const thumb = roi.thumb_url ? resolvePhotoUrl(roi.thumb_url) : null;
  const when = (roi.captured_at || roi.registered_at || '').replace('T', ' ').slice(0, 16);
  const compared = !!roi.reference_label;
  return (
    <li style={cardStyle}>
      <article aria-labelledby={`${id}-name`} data-testid={id}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          {thumb && <img src={thumb} alt="" width={80} height={60} style={thumbStyle} loading="lazy" />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <strong id={`${id}-name`} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span aria-hidden="true" style={{ ...swatchStyle, background: roi.colour || 'transparent' }} />
              <bdi>{roi.label}</bdi>
            </strong>
            {roi.target && <span style={S.hint}>{recordName(t, roi.target)}</span>}
            <span style={S.hint}><bdi dir="ltr">{when}</bdi></span>
          </div>
        </div>
        <dl style={dlStyle}>
          <dt style={dtStyle}>{t('analytics.thermal.values')}</dt>
          <dd style={ddStyle} data-testid={`${id}-values`}>
            <bdi dir="ltr">{t('analytics.thermal.range', { min: formatCelsius(roi.t_min_c, 2), max: formatCelsius(roi.t_max_c, 2),
              avg: formatCelsius(roi.t_avg_c, 2) })}</bdi>
          </dd>
          {compared && (
            <>
              <dt style={dtStyle}>{t('analytics.thermal.deltas', { reference: roi.reference_label })}</dt>
              <dd style={ddStyle} data-testid={`${id}-deltas`}>
                <bdi dir="ltr">{t('analytics.thermal.deltaValues', { min: formatDelta(roi.delta_min_c, 2),
                  max: formatDelta(roi.delta_max_c, 2), avg: formatDelta(roi.delta_avg_c, 2) })}</bdi>
              </dd>
            </>
          )}
          {roi.treatment && <><dt style={dtStyle}>{t('analytics.thermal.treatment')}</dt><dd style={ddStyle} data-testid={`${id}-treatment`}><bdi>{roi.treatment}</bdi></dd></>}
          {roi.unit && <><dt style={dtStyle}>{t('analytics.thermal.unit')}</dt><dd style={ddStyle} data-testid={`${id}-unit`}><bdi>{roi.unit.label}</bdi></dd></>}
          {roi.protocol && (
            <><dt style={dtStyle}>{t('analytics.thermal.protocol')}</dt>
              <dd style={ddStyle} data-testid={`${id}-protocol`}>
                <bdi>{roi.protocol.name || ''}</bdi> <bdi dir="ltr">{t('analytics.thermal.version', { version: roi.protocol.version })}</bdi>
              </dd></>
          )}
          {roi.lid_state === 'closed' && <><dt style={dtStyle}>{t('analytics.thermal.lid')}</dt><dd style={ddStyle}>{t('analytics.thermal.lidClosed')}</dd></>}
          {roi.culture && (
            // The same dish measured on FLIR's photograph (workbench part 4, migration 0103): the region shows both.
            <><dt style={dtStyle}>{t('analytics.thermal.growth', { code: roi.culture.plate_code || '' })}</dt>
              <dd style={ddStyle} data-testid={`${id}-culture`}>
                <bdi dir="ltr">{t('analytics.thermal.growthValues', { clean: fixed(roi.culture.clean_area_cm2, 3),
                  colony: fixed(roi.culture.colony_area_cm2, 3), radius: fixed(roi.culture.mean_radius_mm, 2),
                  contamination: fixed(roi.culture.contamination_pct, 1) })}</bdi>
                {roi.culture.health_status && <> · {t(`analytics.thermal.health.${roi.culture.health_status}`)}</>}
              </dd></>
          )}
        </dl>
        <RoiHistogram histogram={roi.histogram} colour={roi.colour} testId={`${id}-histogram`} />
      </article>
    </li>
  );
}

const fixed = (v, digits) => (Number.isFinite(v) ? v.toFixed(digits) : '–');
const sectionStyle = { display: 'flex', flexDirection: 'column', gap: 8 };
const headingStyle = { margin: 0, fontSize: 15 };
const listStyle = { margin: 0, paddingInlineStart: 18, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 };
const cardsStyle = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10,
                     gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))' };
const cardStyle = { border: '1px solid var(--c-border)', borderRadius: 8, padding: 10, background: 'var(--c-surface)',
                    display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 };
const thumbStyle = { width: 80, height: 60, objectFit: 'cover', borderRadius: 6, background: '#000', flex: 'none' };
const swatchStyle = { display: 'inline-block', width: 12, height: 12, borderRadius: 3, flex: 'none',
                      border: '1px solid var(--c-border-strong)' };
// Each label above its value: the values (two-decimal readings, a protocol's name) get the card's whole width.
const dlStyle = { margin: '6px 0', fontSize: 12 };
const dtStyle = { color: 'var(--c-text-muted-fg)', fontSize: 11 };
const ddStyle = { margin: '0 0 6px', overflowWrap: 'anywhere' };
